import { useEffect, useState, useRef } from 'react';

interface UseCounterOptions {
  target: number;
  duration?: number;
  startOnVisible?: boolean;
  isVisible?: boolean;
  delay?: number;
}

export function useCounter({
  target,
  duration = 2000,
  startOnVisible = true,
  isVisible = true,
  delay = 0,
}: UseCounterOptions) {
  const [count, setCount] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const hasStartedRef = useRef(false);

  // Reset when target changes
  useEffect(() => {
    setCount(0);
    hasStartedRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [target]);

  useEffect(() => {
    // Don't start if not visible
    if (!startOnVisible || !isVisible) {
      return;
    }

    // Don't start if already started
    if (hasStartedRef.current) {
      return;
    }

    // Clean up any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    hasStartedRef.current = true;
    const startTime = Date.now() + delay;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;

      if (elapsed < 0) {
        // Still waiting for delay
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      if (elapsed >= duration) {
        setCount(target);
        animationFrameRef.current = null;
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      // Easing function for smooth animation (ease-out cubic)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOut * target);

      setCount(currentCount);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [target, duration, startOnVisible, isVisible, delay]);

  return count;
}

